import React from 'react';
import { graphql } from 'gatsby';
//@ts-ignore
import * as styles from './global-masthead.module.css';
import CtaLink from '../Global/CtaLink';
import { BuildLinkHref } from '../Global/BuildLinkHref';
import {
    KontentStringValue,
    CTAConfiguration,
    GeneralImages,
    KontentRadioMultiChoice,
    KontentMedia,
    BrandColor,
    KontentNumberValue,
    CtaTsType,
    KontentStringCodenameValue,
    KontentRichText,
} from '../../../types/KontentProps';
import {
    getColor,
    getMediaData,
    getMultiChoice,
    optimizeKontentImageUrl,
} from '../Global/DataUtils';
import { getImageData } from '../Global/DataUtils/gatsbyImageUtils';
import CustomHeadline from '../Global/CustomHeadline';
import AnchorLink from '../Global/AnchorLink';
import ArtDirectedImage from '../Global/ArtDirectedImage';

type ContentPositionType = 'right' | 'left' | 'center' | undefined;
type MastheadStyles = 'large' | 'short';
type ShowHide = 'show' | 'hide';
type HeadlineSource = 'page_title' | 'display_headline_field';
type GlobalMastheadVerticalPosition = 'top' | 'center' | 'bottom';

const GLOBAL_MASTHEAD_VERTICAL_DEFAULT: GlobalMastheadVerticalPosition =
    'center';

function resolveGlobalMastheadVerticalPosition(
    field?: KontentRadioMultiChoice<GlobalMastheadVerticalPosition>
): GlobalMastheadVerticalPosition {
    const value = getMultiChoice(field);
    return value === 'top' || value === 'center' || value === 'bottom'
        ? value
        : GLOBAL_MASTHEAD_VERTICAL_DEFAULT;
}

interface Props extends CTAConfiguration, GeneralImages {
    kontentItemId?: string;
    component_anchor_name?: KontentStringValue;
    content_position?: KontentRadioMultiChoice<ContentPositionType>;
    cta_show_or_hide: KontentRadioMultiChoice<ShowHide>;
    display_headline: KontentStringValue;
    display_sub_headline?: KontentStringValue;
    masthead_style: KontentRadioMultiChoice<MastheadStyles>;
    masthead_video?: KontentMedia;
    page_headline_source?: KontentRadioMultiChoice<HeadlineSource>;
    text_background_color?: BrandColor;
    text_background_opacity?: KontentNumberValue;
    under_image_bar_color?: BrandColor;
    under_image_bar_show_or_hide: KontentRadioMultiChoice<ShowHide>;
    tablet_image: KontentMedia;
    cta2_show_or_hide?: KontentRadioMultiChoice<ShowHide>;
    cta2?: {
        value: CtaTsType[];
    };
    display_headline_tag: KontentStringCodenameValue;
    display_sub_headline_tag?: KontentStringCodenameValue;
    description_copy?: KontentRichText;
    description_tag?: KontentStringCodenameValue;
    sub_headline_style?: KontentStringCodenameValue;
    description_style?: KontentStringCodenameValue;
    enable_trademark_styling?: KontentRadioMultiChoice<'yes' | 'no'>;
    vertical_alignment_desktop?: KontentRadioMultiChoice<GlobalMastheadVerticalPosition>;
    vertical_alignment_mobile?: KontentRadioMultiChoice<GlobalMastheadVerticalPosition>;
    font_color?: BrandColor;
}

const GlobalMasthead: React.FC<Props> = ({
    kontentItemId,
    component_anchor_name,
    cta_configuration__cta_label,
    cta_configuration__cta_location,
    cta_configuration__cta_location_anchor_point,
    cta_configuration__cta_window_action,
    content_position,
    cta_show_or_hide,
    display_headline,
    display_sub_headline,
    general_large___small_image_selection__large,
    general_large___small_image_selection__small,
    masthead_style,
    masthead_video,
    page_headline_source,
    text_background_color,
    text_background_opacity,
    under_image_bar_color,
    under_image_bar_show_or_hide,
    tablet_image,
    cta2_show_or_hide,
    cta2,
    display_headline_tag,
    display_sub_headline_tag,
    description_copy,
    description_tag,
    sub_headline_style,
    description_style,
    enable_trademark_styling,
    vertical_alignment_desktop,
    vertical_alignment_mobile,
    font_color,
}) => {
    const SecondCta = cta2?.value[0]?.elements;
    const anchorName = component_anchor_name?.value;
    const underImageBarShowHide =
        getMultiChoice(under_image_bar_show_or_hide) === 'show';
    const mastheadStyle = getMultiChoice(masthead_style);
    const textFontColor = getColor(font_color) || 'var(--color-white)';
    const mastheadBaseHeight = mastheadStyle === 'short' ? '321px' : '100vh';
    const mastheadAdjustedHeight =
        underImageBarShowHide && mastheadStyle === 'large'
            ? { height: `calc(${mastheadBaseHeight} - 100px)` }
            : { height: mastheadBaseHeight };
    const mastheadHeightStyle =
        mastheadStyle === 'short'
            ? { marginTop: '96px', ...mastheadAdjustedHeight }
            : mastheadAdjustedHeight;
    const subHeadlineStyle = sub_headline_style?.value[0]?.codename;
    const descriptionStyle = description_style?.value[0]?.codename;
    const video = getMediaData(masthead_video);
    const contentPosition = getMultiChoice(content_position);
    const globalMastheadVerticalAlignDesktop =
        resolveGlobalMastheadVerticalPosition(vertical_alignment_desktop);
    const globalMastheadVerticalAlignMobile =
        resolveGlobalMastheadVerticalPosition(vertical_alignment_mobile);
    const textBackgroundColor = getColor(text_background_color);
    const textBackgroundOpacity = text_background_opacity?.value
        ? text_background_opacity.value / 100
        : 1;
    const ctaShowHide =
        getMultiChoice(cta_show_or_hide) === 'show' &&
        cta_configuration__cta_label?.value;
    const cta2ShowHide =
        getMultiChoice(cta2_show_or_hide) === 'yes' &&
        SecondCta?.cta_label?.value;

    const ctaWindowAction = getMultiChoice(
        cta_configuration__cta_window_action
    );
    const cta2windowAction = getMultiChoice(SecondCta?.cta_window_action);
    const anchorPointLink = cta_configuration__cta_location_anchor_point?.value;
    const mainHeadline = display_headline?.value;
    const isSingleLongWord =
        !!mainHeadline &&
        mainHeadline.indexOf(' ') === -1 &&
        mainHeadline.length >= 12;

    const innerContentSpacingStyles = {
        margin: '15px 0',
    };

    const innerContentClassName = `${styles.innerContent} ${
        contentPosition === 'right'
            ? styles.innerContentRight
            : contentPosition === 'center'
            ? styles.innerContentCenter
            : ''
    }`;

    const renderImages = () => {
        const largeImageData = getImageData(
            general_large___small_image_selection__large
        );
        const smallImageData = general_large___small_image_selection__small
            ?.value[0]
            ? getImageData(general_large___small_image_selection__small)
            : null;
        const tabletImageData = tablet_image?.value[0]
            ? getImageData(tablet_image)
            : null;

        if (!largeImageData?.image) return null;

        const isShort = mastheadStyle === 'short';
        const largeW = isShort ? 1920 : 1920;
        const largeH = isShort ? 321 : 988;
        const smallW = isShort ? 720 : 720;
        const smallH = isShort ? 512 : 1080;
        const tabletW = isShort ? 1024 : 1024;
        const tabletH = isShort ? 321 : 1366;

        const sources = [];
        if (smallImageData?.image) {
            sources.push({
                url: smallImageData.image.url,
                media: '(max-width: 576px)',
                width: smallW,
                height: smallH,
                quality: 95,
            });
        }
        if (tabletImageData?.image) {
            sources.push({
                url: tabletImageData.image.url,
                media: '(max-width: 1024px)',
                width: tabletW,
                height: tabletH,
                quality: 95,
            });
        } else if (smallImageData?.image) {
            sources.push({
                url: largeImageData.image.url,
                media: '(max-width: 1024px)',
                width: tabletW,
                height: tabletH,
                quality: 95,
            });
        }
        sources.push({
            url: largeImageData.image.url,
            media: smallImageData?.image
                ? '(min-width: 1025px)'
                : '(min-width: 1px)',
            width: largeW,
            height: largeH,
            quality: 95,
        });

        return (
            <ArtDirectedImage
                sources={sources}
                fallback={{
                    url: largeImageData.image.url,
                    width: largeW,
                    height: largeH,
                    quality: 95,
                }}
                alt={largeImageData.alt}
                className={styles.picture}
                style={{ overflow: 'visible' }}
                imgStyle={{ height: mastheadBaseHeight }}
                loading="eager"
                fetchPriority="high"
                elementCodename="general_large___small_image_selection__large"
            />
        );
    };

    const renderVideo = () => {
        const largeImageData = getImageData(
            general_large___small_image_selection__large
        );
        const posterUrl = largeImageData?.image?.url
            ? optimizeKontentImageUrl(largeImageData.image.url, {
                  w: 960,
                  fm: 'webp',
                  q: 80,
              })
            : undefined;
        return (
            <video
                muted={true}
                autoPlay={true}
                loop={true}
                playsInline={true}
                poster={posterUrl}
                preload="none"
                width={1920}
                height={mastheadStyle === 'short' ? 321 : 988}
                aria-hidden="true"
            >
                <source
                    src={video?.url}
                    type="video/mp4"
                    media="(min-width: 768px)"
                ></source>
            </video>
        );
    };

    const fetchHeadlineStyle = (headlineStyle: String) => {
        if (headlineStyle == 'small') {
            return 'h5';
        } else if (headlineStyle == 'medium') {
            return 'h4';
        } else {
            return 'h3';
        }
    };

    const headlineClassName = isSingleLongWord
        ? styles.headlineLongWord
        : styles.headlineDefault;

    return (
        <section
            id={anchorName || ''}
            className={styles.wrapper}
            style={mastheadHeightStyle}
            data-kontent-item-id={kontentItemId}
        >
            <div
                className={styles.contentContainer}
                style={{ height: mastheadHeightStyle.height }}
            >
                {video?.url ? renderVideo() : renderImages()}
                {textBackgroundColor && (
                    <div
                        className={styles.bgOverlay}
                        style={{
                            backgroundColor: textBackgroundColor,
                            opacity: textBackgroundOpacity,
                        }}
                    ></div>
                )}
            </div>
            <div
                className={styles.innerContentContainer}
                data-global-masthead-vertical-align-desktop={
                    globalMastheadVerticalAlignDesktop
                }
                data-global-masthead-vertical-align-mobile={
                    globalMastheadVerticalAlignMobile
                }
                data-global-masthead-style={mastheadStyle}
            >
                <div
                    className={innerContentClassName}
                    style={
                        {
                            '--global-masthead-font-color': textFontColor,
                        } as React.CSSProperties
                    }
                >
                    <div
                        style={
                            display_sub_headline?.value
                                ? innerContentSpacingStyles
                                : {
                                      ...innerContentSpacingStyles,
                                      display: 'none',
                                  }
                        }
                    >
                        <CustomHeadline
                            elementCodename="display_sub_headline"
                            style={
                                subHeadlineStyle
                                    ? fetchHeadlineStyle(subHeadlineStyle)
                                    : 'h5'
                            }
                            tag={
                                display_sub_headline_tag?.value[0]?.codename
                                    ? display_sub_headline_tag
                                    : {
                                          value: [
                                              {
                                                  codename: 'h2',
                                                  name: 'h2',
                                              },
                                          ],
                                      }
                            }
                            headline_value={
                                display_sub_headline || { value: '' }
                            }
                            overrideStyles={{
                                fontFamily: 'var(--subheading-family)',
                                letterSpacing: '0.1em',
                                color: 'inherit',
                            }}
                            className={styles.subheadlineOverride}
                        />
                    </div>
                    <div
                        style={
                            display_headline?.value
                                ? {
                                      marginBottom: description_copy?.value
                                          ? '30px'
                                          : '15px',
                                  }
                                : {}
                        }
                    >
                        <CustomHeadline
                            elementCodename="display_headline"
                            style={'h1'}
                            tag={
                                display_headline_tag?.value[0]?.codename
                                    ? display_headline_tag
                                    : {
                                          value: [
                                              { codename: 'h1', name: 'h1' },
                                          ],
                                      }
                            }
                            headline_value={display_headline}
                            enable_trademark_styling={enable_trademark_styling}
                            overrideStyles={{
                                margin: '0px 0px',
                                fontFamily: 'var(--font-avenir)',
                                lineHeight: '1.2',
                                color: 'inherit',
                            }}
                            className={headlineClassName}
                        />
                    </div>

                    <div
                        style={
                            description_copy?.value
                                ? innerContentSpacingStyles
                                : {
                                      ...innerContentSpacingStyles,
                                      display: 'none',
                                  }
                        }
                    >
                        <CustomHeadline
                            elementCodename="description_copy"
                            style={
                                descriptionStyle
                                    ? fetchHeadlineStyle(descriptionStyle)
                                    : 'h5'
                            }
                            tag={
                                description_tag?.value[0]?.codename
                                    ? description_tag
                                    : {
                                          value: [
                                              {
                                                  codename: 'h2',
                                                  name: 'h2',
                                              },
                                          ],
                                      }
                            }
                            headline_value={description_copy || { value: '' }}
                            overrideStyles={{
                                fontFamily: 'var(--font-barlow-regular)',
                                letterSpacing: '0',
                                color: 'inherit',
                            }}
                            className={styles.descriptionOverride}
                        />
                    </div>
                    <div
                        className={`${styles.ctas} ${
                            cta2ShowHide && styles.ctasWithSameWidth
                        }`}
                        style={innerContentSpacingStyles}
                    >
                        <div className={styles.cta1}>
                            {mastheadStyle === 'large' && ctaShowHide ? (
                                !cta_configuration__cta_location?.value
                                    ?.length && anchorPointLink ? (
                                    <AnchorLink
                                        targetId={anchorPointLink}
                                        visualStyle="outlineLight"
                                    >
                                        {cta_configuration__cta_label?.value}
                                    </AnchorLink>
                                ) : (
                                    <CtaLink
                                        href={BuildLinkHref(
                                            cta_configuration__cta_location,
                                            cta_configuration__cta_location_anchor_point
                                        )}
                                        target={
                                            ctaWindowAction === 'new_window'
                                                ? '_blank'
                                                : undefined
                                        }
                                        visualStyle="outlineLight"
                                    >
                                        {cta_configuration__cta_label?.value}
                                    </CtaLink>
                                )
                            ) : null}
                        </div>
                        {mastheadStyle === 'large' && cta2ShowHide ? (
                            <div className={styles.cta2}>
                                <CtaLink
                                    href={BuildLinkHref(
                                        SecondCta?.cta_location,
                                        SecondCta?.cta_location_anchor_point
                                    )}
                                    target={
                                        cta2windowAction === 'new_window'
                                            ? '_blank'
                                            : undefined
                                    }
                                    visualStyle="outlineLight"
                                    elementCodename="cta_label"
                                >
                                    {SecondCta?.cta_label.value}
                                </CtaLink>
                            </div>
                        ) : null}
                    </div>
                </div>
            </div>
            {underImageBarShowHide && (
                <div
                    className={styles.underImageBar}
                    style={{
                        backgroundColor: getColor(under_image_bar_color) as any,
                    }}
                ></div>
            )}
        </section>
    );
};

export default GlobalMasthead;

export const fragmentGlobalMasthead = graphql`
    fragment GlobalMasthead on kontent_item_component___global_masthead {
        id
        elements {
            display_headline {
                value
            }
            component_anchor_name {
                value
            }
            content_position {
                value {
                    codename
                }
            }
            vertical_alignment_desktop {
                value {
                    codename
                }
            }
            vertical_alignment_mobile {
                value {
                    codename
                }
            }
            cta_configuration__cta_label {
                value
            }
            cta_configuration__cta_location_anchor_point {
                value
            }
            cta2_show_or_hide {
                value {
                    codename
                }
            }
            cta2 {
                value {
                    ...kontentCta
                }
            }
            cta_configuration__cta_location {
                ...kontentCtaLocation
            }
            cta_configuration__cta_window_action {
                value {
                    codename
                }
            }
            cta_show_or_hide {
                value {
                    codename
                }
            }
            display_sub_headline {
                value
            }
            display_headline_tag {
                value {
                    codename
                    name
                }
            }
            display_sub_headline_tag {
                value {
                    codename
                    name
                }
            }
            description_copy {
                value
            }
            description_tag {
                value {
                    codename
                    name
                }
            }
            general_large___small_image_selection__large {
                ...kontentImage
            }
            general_large___small_image_selection__small {
                ...kontentImage
            }
            tablet_image {
                ...kontentImage
            }
            masthead_style {
                value {
                    codename
                }
            }
            masthead_video {
                value {
                    ... on kontent_item_media___masthead_video {
                        elements {
                            file {
                                value {
                                    url
                                    description
                                    name
                                }
                            }
                        }
                    }
                }
            }
            page_headline_source {
                value {
                    codename
                }
            }
            text_background_color {
                ...kontentColor
            }
            text_background_opacity {
                value
            }
            under_image_bar_color {
                ...kontentColor
            }
            under_image_bar_show_or_hide {
                value {
                    codename
                }
            }
            sub_headline_style {
                value {
                    codename
                    name
                }
            }
            description_style {
                value {
                    codename
                    name
                }
            }
            enable_trademark_styling {
                value {
                    codename
                }
            }
            font_color {
                ...kontentColor
            }
        }
    }
`;
